import { AfterViewInit, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Call } from '@twilio/voice-sdk';
import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';
import { Observable, Subject, Subscription } from 'rxjs';
import { map, retry, take, takeUntil, tap } from 'rxjs/operators';
import { clearsMicrophoneAlarm, raisesMicrophoneAlarm } from 'src/app/models/call-audio-warning.model';
import { ClinicalResolution } from 'src/app/models/clinical.model';
import { CallQuality, INITIAL_CALL_QUALITY } from 'src/app/models/call-quality.model';
import {
  isBlockingMicrophoneFailure,
  isReportableMicrophoneFailure,
  MICROPHONE_CHECK_MESSAGES,
  MicrophoneCheckResult,
} from 'src/app/models/microphone-check.model';
import {
  isMicrophoneProblem,
  isMicrophoneUnavailable,
  MICROPHONE_PROBLEM_HELP,
  MicrophoneAvailability,
} from 'src/app/models/microphone-availability.model';
import { CONTACT_DAY_ORDER, Patient, PatientContact } from 'src/app/models/patient.model';
import { User } from 'src/app/models/user.model';
import { NewAuthService } from 'src/app/services/auth/new-auth-service.service';
import { CallQualityService } from 'src/app/services/communications/call-quality.service';
import { MicrophoneAvailabilityService } from 'src/app/services/communications/microphone-availability.service';
import { TwilioService } from 'src/app/services/communications/twilio.service';
import { DataService } from 'src/app/services/data.service';
import { FirestoreService } from 'src/app/services/firestore.service';
import { PatientCommunicationType } from 'src/app/services/models/patient-communication.model';
import { PatientCommunicationService } from 'src/app/services/patient-communication/patient-communication.service';
import { PatientService } from 'src/app/services/patient.service';
import { SnackService } from 'src/app/services/snack.service';
import { UtilsService } from 'src/app/services/utils.service';
import { ShowOnTouchErrorStateMatcher } from '../../errors/error-state-matcher';
import { SurveyDialogComponent } from '../survey-dialog/survey-dialog.component';
import { Client } from 'src/app/models/client.model';
import { getActiveClientPhoneConfigs } from 'src/app/models/client.util';
import { OutboundCallerIdDecision } from '../../models/caller-id-decision.model';
import { PhoneConfig, PhoneConfigEntry } from '../../models/phone-config.model';
import { UserService } from 'src/app/services/user.service';
import { VoicemailMessage, VOICEMAIL_SELECTION } from 'src/app/models/voicemail-message.model';
import { CALL_READINESS_MESSAGES, CallReadiness } from 'src/app/models/twilio-device.model';
import { VoicemailDropCanaryService } from 'src/app/services/communications/voicemail-drop-canary.service';
import { VoicemailMessagesService } from 'src/app/services/communications/voicemail-messages.service';
import { OutboundCallerIdCanaryService } from 'src/app/services/outbound-caller-id-canary.service';
import { LastPatientCallService } from 'src/app/services/calls/last-patient-call.service';

const WELBY_LINE_LABEL = 'Your Welby line';
const CLIENT_LINE_LABEL = 'Client line';

@Component({
  selector: 'app-call-patient-dialog',
  templateUrl: './call-patient-dialog.component.html',
  styleUrls: ['./call-patient-dialog.component.scss'],
})
export class CallPatientDialogComponent implements OnInit, AfterViewInit, OnDestroy {
  existingPatient: boolean;
  patientContacts$: Observable<PatientContact[]>;
  patientId: string;
  callForm: UntypedFormGroup;
  phoneTypes: any[] = [];
  callInProgress = false;
  callingInProgress = false;
  provider: User;
  duration = 0;
  callConnected = false;
  currentCall: Call;
  durationInterval;
  durationFormatted = '00:00';
  contentVisible = true;
  unsubscribe = new Subject<void>();
  shouldRemind = false;
  shouldRemindRecordConsent = false;
  dragPosition = { x: 0, y: 0 };
  isMuted = false;
  /**
   * Set while the SDK reports the care manager's input level as flat during a live call: the patient
   * is hearing silence. Shown in the call panel because by then the call is already up — there is
   * nothing left to abort, only something to tell them.
   */
  microphoneWarning: string | null = null;
  /** Whether a browser call can get a microphone, known before Call is pressed. */
  microphoneAvailability: MicrophoneAvailability = 'unknown';
  /** Signal of the browser leg while a call is up. */
  callQuality: CallQuality = INITIAL_CALL_QUALITY;
  isOnHold = false;
  holdPending = false;
  showAddParticipant = false;
  parentCallSid: string;
  /** Set once the backend accepted this call's survey registration. */
  postCallSurveyRegistered = false;
  additionalParticipantSessionId: string;
  additionalParticipantNumber: string;
  additionalParticipantCallSid: string;
  additionalParticipantPhoneNumberControl = new UntypedFormControl(null, [
    Validators.required,
    this.validatePhoneNumber.bind(this),
  ]);
  errorStateMatcher = new ShowOnTouchErrorStateMatcher();
  isSingleTwilioLinePerClient: boolean;
  /**
   * One number per client the *staff member* belongs to — not several numbers of one client.
   * Populated from `getClientPhones(userId)` and only used for external calls. Unrelated to
   * `clinicPhoneOptions` below, despite the similar shape.
   */
  clientPhoneConfigList: PhoneConfig[];
  /** The active lines of the *patient's* client, for the staff member to pick from. */
  clinicPhoneOptions: PhoneConfigEntry[] = [];
  clinicPhoneNumber: string | null = null;
  /** True while the caller ID is being resolved asynchronously (avoids showing a number that changes right after). */
  callerIdResolving = false;
  /**
   * Name of the patient's client, for the source label.
   *
   * Captured from the `clients/{id}` document rather than derived, because it is the only thing the
   * panel needs from that document. Everything else it shows is read back from the form control, so
   * whoever resolves the caller ID stays the single writer — see {@link callerIdSourceLabel}.
   */
  clientName: string | null = null;
  /** True when the patient's client is configured to call from its own line. */
  clientRequiresOwnLine = false;
  /**
   * Snapshot of the caller ID taken at dial time: EXACTLY the value sent as `from`.
   * Immutable while the call lasts — the pre-call resolution is fed by a live Firestore stream
   * (`clients/{id}`) that can emit again if an admin edits the phoneConfig during the call.
   */
  dialedCallerId: string | null = null;
  /** Snapshot of {@link callerIdSourceLabel} taken with {@link dialedCallerId}: the two must agree. */
  dialedCallerIdSourceLabel: string | null = null;
  /** Pre-recorded messages offered alongside the live and no-message options. */
  voicemailMessages$: Observable<VoicemailMessage[]>;
  /** Exposed so the template can name the two non-catalogue selections. */
  readonly voicemailSelection = VOICEMAIL_SELECTION;

  private participantDestroy$?: Subject<void>;
  /** Cancels an in-flight hold request; internal, so it lives with the private members. */
  private readonly holdCancel$ = new Subject<void>();
  private callQualitySubscription: Subscription | null = null;

  /**
   * Set when the dialog closes. browserCall() can wait several seconds for the Device (takeover,
   * token, registration); a dialog closed meanwhile must not go on to dial a call nobody can see.
   */
  private destroyed = false;

  constructor(
    public twilioService: TwilioService,
    public dialog: MatDialog,
    public utilService: UtilsService,
    @Inject(MAT_DIALOG_DATA) public data,
    private dialogRef: MatDialogRef<CallPatientDialogComponent>,
    private fsService: FirestoreService,
    private fb: UntypedFormBuilder,
    private dataService: DataService,
    private patientService: PatientService,
    private authService: NewAuthService,
    private snackBarService: SnackService,
    private communicationService: PatientCommunicationService,
    private userService: UserService,
    private outboundCallerIdCanary: OutboundCallerIdCanaryService,
    private voicemailMessagesService: VoicemailMessagesService,
    private voicemailDropCanary: VoicemailDropCanaryService,
    private microphoneAvailabilityService: MicrophoneAvailabilityService,
    private callQualityService: CallQualityService,
    private lastPatientCall: LastPatientCallService
  ) {
    this.initializeForm();
    this.voicemailMessages$ = this.voicemailMessagesService.getActiveMessages();
  }

  /**
   * Whether this call's caller ID is chosen automatically, with no dropdown for the staff.
   *
   * Both halves are required, and they answer different questions:
   *
   *  - the canary says whether this **user** is in the pilot that stopped choosing by hand;
   *  - a non-empty `clinicPhoneOptions` says whether this **patient's client** actually has lines to
   *    derive from. It is only populated when the client carries `isSingleTwilioLinePerClient` and has
   *    at least one active line, so it already encodes that condition.
   *
   * Requiring both is what keeps a piloted nurse from being stranded: called about a patient whose
   * clinic has not been configured yet, she falls back to the dropdown she had before rather than to a
   * dialog with no way to pick a number.
   */
  /**
   * Whether this staff member may choose what happens if the call reaches voicemail.
   *
   * Gates the control *and* the parameter sent with the call, which is what makes it a real kill
   * switch: the call processor only runs answering machine detection when a selection arrives, so for
   * everyone outside the pilot the call is placed exactly as it was before this feature — no
   * detection, no per-call charge, no extra webhook.
   */
  get canChooseVoicemailMessage(): boolean {
    return this.voicemailDropCanary.isEnabled;
  }

  get usesAutomaticCallerId(): boolean {
    return this.outboundCallerIdCanary.isEnabled && this.clinicPhoneOptions.length > 0;
  }

  /**
   * The canary for THIS story: whether the caller ID may be shown at all.
   *
   * Two independent validations, both required (Valentina, Slack 2026-08-10):
   *
   *  - **the user** — their email is listed in `welby_configuration/featureToggles.outboundCallerIdCanary`
   *    (`emails`), or `allowAll: true` opens it to everyone. This is Andrés Mosquera's canary from
   *    NP-04, reused as-is through {@link OutboundCallerIdCanaryService}, so one Firestore doc drives
   *    both the rollout of the automatic selection and the rollout of this display.
   *  - **the clinic** — the patient's client carries `featureToggles.isSingleTwilioLinePerClient`, i.e.
   *    it is on the "call from the client's own line" flow. Read off the `clients/{id}` document via
   *    {@link clientRequiresOwnLine}; NOT the identically named flag on the logged-in user, which gates
   *    the external-call selector instead (see `ngOnInit`).
   *
   * Fails closed the same way the service does: while the config is still resolving, or if either
   * validation is not met, the dialog renders exactly as it did before this story — no panel, and the
   * Call button back to its previous enablement rule (see {@link callerIdDisablesCallButton}).
   *
   * Deliberately **not** keyed on `clinicPhoneOptions`: a client that is on the flow but has no active
   * line is precisely the degraded case the panel exists to state ({@link clientLineUnavailable}), so
   * gating on the option list would hide the one message that explains why the patient is about to see
   * the care manager's number.
   */
  get callerIdVisibilityEnabled(): boolean {
    return this.outboundCallerIdCanary.isEnabled && this.clientRequiresOwnLine;
  }

  /** True when the number comes from the per-client selector (external call with the toggle on). */
  get usesClientLineSelector(): boolean {
    return !!this.isSingleTwilioLinePerClient && !!this.data?.isExternal;
  }

  /**
   * Effective caller ID for the PRE-CALL phase: the value that will be sent as `from`.
   * markus-call-processor's `VoiceService.makeVoiceCall` uses it verbatim as `<Dial callerId>`.
   */
  get effectiveCallerId(): string {
    const controlName = this.usesClientLineSelector ? 'twilioNumberPerClient' : 'twilioNumber';
    return this.callForm.get(controlName)?.value || '';
  }

  /** What gets displayed: the effective value before dialing, the snapshot once dialed. */
  get displayedCallerId(): string {
    if (this.callingInProgress || this.callInProgress) {
      return this.dialedCallerId || '';
    }
    return this.effectiveCallerId;
  }

  /**
   * Source label for the current phase. Snapshotted alongside the number, because the label is
   * re-derived from two live streams (`users/{uid}` via currentUser$, and `clients/{id}`): otherwise a
   * mid-call emission could flip it to "Your Welby line" while the frozen number is the clinic's.
   */
  get displayedCallerIdSourceLabel(): string | null {
    if (this.callingInProgress || this.callInProgress) {
      return this.dialedCallerIdSourceLabel;
    }
    return this.callerIdSourceLabel;
  }

  /**
   * Where the displayed caller ID comes from. Derived, never assigned.
   *
   * Deliberately reads back the form control instead of being set wherever the number is resolved:
   * that keeps a single writer for the caller ID and makes this panel a pure view of it. NP-04
   * (welby-web #1200) resolves the number by asking the backend for the line nearest to the patient's
   * state and writing it to the same `twilioNumber` control, so this label follows that decision
   * without having to be taught about it.
   *
   * It deliberately says *whose* line it is and not *why the rule picked it*:
   * `CallerIdDecision.reason` stays out of the UI, as NP-04 decided.
   */
  get callerIdSourceLabel(): string | null {
    if (this.usesClientLineSelector) {
      const selected = this.callForm.get('twilioNumberPerClient')?.value;
      const config = this.clientPhoneConfigList?.find((c) => c.phoneNumber === selected);
      return config?.clientName || CLIENT_LINE_LABEL;
    }
    if (this.isClientOwnLine(this.effectiveCallerId)) {
      return this.clientName || CLIENT_LINE_LABEL;
    }
    return WELBY_LINE_LABEL;
  }

  /**
   * The client is set up to call from its own line but has no active one, so the call degrades to the
   * care manager's line. Not surfaced on its own — the panel shows the line that will actually be
   * used; it only sharpens {@link callerIdMissingMessage} when there is no fallback either.
   *
   * Keyed on the option list, not on `clinicPhoneNumber`: after NP-04 a client can hold several lines
   * and still leave `clinicPhoneNumber` null when the derivation cannot choose between them. That is
   * "you pick" ({@link awaitingCallerLineChoice}), a different state from "there is nothing to pick".
   */
  get clientLineUnavailable(): boolean {
    return this.clientRequiresOwnLine && !this.clinicPhoneOptions.length;
  }

  /**
   * The client has lines, the dropdown is on screen, and none is selected — so the staff has to pick.
   *
   * A distinct state from "no caller ID": the remedy is one click in this dialog, not an
   * administrator. It is reached when the backend cannot decide — no primary address on the patient,
   * a state it cannot parse, or no line authorized for it — and the client has more than one line.
   */
  get awaitingCallerLineChoice(): boolean {
    return (
      !this.usesClientLineSelector &&
      !this.usesAutomaticCallerId &&
      !this.effectiveCallerId &&
      this.clinicPhoneOptions.length > 0
    );
  }

  /**
   * The automatic flow is on but no line came out of it, so there is nothing to name.
   *
   * NP-04 chose to let that call go out rather than refuse it: `from` reaches the TwiML webhook empty
   * and markus-call-processor falls back to `TWILIO_AUTOMATED_LINE`
   * (`voice.service.ts resolveOutboundCallerId`, welby-micro-service #909 / markus #67). That decision
   * stands here — this feature only refuses to claim a number it does not know, and says which line
   * will actually be used instead.
   */
  get automaticCallerIdUnresolved(): boolean {
    return this.usesAutomaticCallerId && !this.effectiveCallerId;
  }

  /**
   * Whether the missing caller ID should stop the call.
   *
   * Deliberately false in the automatic flow: NP-04 documents the empty-`from` fallback as the wanted
   * behaviour there, and a canary user has no dropdown to fix it with, so blocking would leave the
   * nurse unable to call at all. Everywhere else there either is a remedy (pick a line) or genuinely
   * nothing to send, and failing early beats an opaque Twilio rejection.
   */
  get blocksCallWithoutCallerId(): boolean {
    return !this.hasCallerId && !this.usesAutomaticCallerId;
  }

  /**
   * Whether the Call button is greyed out over the caller ID — canary-gated, unlike the guard above.
   *
   * The distinction is deliberate. {@link blocksCallWithoutCallerId} and the resolving check both stop
   * a call that would reach Twilio with an empty `from`, and that must keep protecting everyone. What
   * this story added on top is *showing* that refusal ahead of the click: outside the canary the button
   * looks exactly as it did before, and the same guards still answer with a snackbar in `doCall()`.
   */
  get callerIdDisablesCallButton(): boolean {
    return this.callerIdVisibilityEnabled && (this.callerIdResolving || this.blocksCallWithoutCallerId);
  }

  /** Enables the Call button. Always evaluated against the effective value (pre-call phase). */
  get hasCallerId(): boolean {
    return !!this.effectiveCallerId;
  }

  /** True when there is something to show in the current phase. */
  get hasDisplayedCallerId(): boolean {
    return !!this.displayedCallerId;
  }

  /** Human readable format. formatPhoneNumber is never called with an empty value: it would return 'Unknown Number'. */
  get callerIdDisplay(): string {
    return this.hasDisplayedCallerId ? this.utilService.formatPhoneNumber(this.displayedCallerId) : '';
  }

  get isBrowserCall(): boolean {
    return this.callForm.get('via')?.value === 'browser';
  }

  /** A browser call is pointless without a microphone; a call placed from a phone needs none. */
  get microphoneBlocksCall(): boolean {
    return this.isBrowserCall && isMicrophoneUnavailable(this.microphoneAvailability);
  }

  get microphoneBlockedMessage(): string {
    return isMicrophoneUnavailable(this.microphoneAvailability) ? MICROPHONE_PROBLEM_HELP[this.microphoneAvailability].title : '';
  }

  /**
   * The caller-ID panel lives outside `.call-form-fields-container` (which is display:none during the
   * call) so it survives into the in-call phase. It is hidden only when minimized — that state is
   * designed as a single-value pill for the timer — and outside the canary
   * ({@link callerIdVisibilityEnabled}), where the dialog must look untouched by this story.
   */
  get showCallerIdPanel(): boolean {
    return this.callerIdVisibilityEnabled && this.contentVisible && this.isBrowserCall;
  }

  /** The spinner is a pre-call state only: during the call the value shown is a snapshot, never pending. */
  get showCallerIdResolving(): boolean {
    return this.callerIdResolving && !this.callInProgress;
  }

  /** No caller ID to show: renders the error state, and tints the whole block accordingly. */
  get showCallerIdMissing(): boolean {
    return !this.showCallerIdResolving && !this.hasDisplayedCallerId;
  }

  /**
   * The missing state is the staff's to fix, not an error. Tinted and worded differently so a nurse
   * can tell "click the dropdown" apart from "call an administrator" without reading the sentence.
   */
  get showCallerIdChoicePending(): boolean {
    return (
      this.showCallerIdMissing &&
      !this.callInProgress &&
      (this.awaitingCallerLineChoice || this.automaticCallerIdUnresolved)
    );
  }

  /**
   * Phase-appropriate copy for the missing state. "Cannot be placed" is a pre-call statement: saying
   * it while a call is up would be false, and the in-call phase reaches this state only through the
   * teardown window (endCall clears the snapshot before `callInProgress` flips).
   */
  get callerIdMissingMessage(): string {
    if (this.callInProgress) {
      return 'Caller ID unavailable.';
    }
    if (this.awaitingCallerLineChoice) {
      return 'Select the clinic number to call from.';
    }
    if (this.automaticCallerIdUnresolved) {
      return "No clinic number could be selected for this patient — the call will use Welby's default line.";
    }
    if (this.clientLineUnavailable) {
      // Both possible lines are missing, so name both instead of just "no caller ID".
      return 'This client has no active line and you have no Welby line assigned — this call cannot be placed.';
    }
    return 'No caller ID assigned — this call cannot be placed.';
  }

  /**
   * The contact currently selected in the `contactNumber` control, or null.
   *
   * Only the patient-contact select stores a `PatientContact` object; the external-call flow puts a
   * raw phone string in the same control, so the type guard is load-bearing — without it the
   * tooltip getter below would run its day/period logic over a string.
   */
  get selectedContact(): PatientContact | null {
    const value = this.callForm?.get('contactNumber')?.value;
    return value && typeof value === 'object' ? (value as PatientContact) : null;
  }

  /** Same text the options carry, for the closed select trigger. Empty string = nothing to show. */
  get selectedContactTooltip(): string {
    return this.getContactTooltip(this.selectedContact);
  }

  ngAfterViewInit(): void {}

  ngOnInit(): void {
    this.authService.currentUser$.pipe(takeUntil(this.unsubscribe)).subscribe((user) => {
      this.provider = {
        ...user,
        twilio_line: Array.isArray(user.twilio_line) ? user.twilio_line : [user.twilio_line],
      };
      // Read off the **logged-in user**, and it gates the external-call path only. The identically
      // named flag on the **client** document (see `initializeTwilioNumber`) gates something else
      // entirely — the clinic-lines dropdown for patient calls. Same name, two documents, two
      // features, both predating NP-04 (PR #1011). Setting it on the wrong document silently enables
      // the wrong flow, so check which one you mean before touching either.
      this.isSingleTwilioLinePerClient = user.featureToggles?.isSingleTwilioLinePerClient;

      this.initializeTwilioNumber();
    });

    this.microphoneAvailabilityService.availability$
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((availability) => (this.microphoneAvailability = availability));

    this.twilioService.audioWarnings$.pipe(takeUntil(this.unsubscribe)).subscribe((warning) => {
      if (clearsMicrophoneAlarm(warning)) {
        this.microphoneWarning = null;
      } else if (raisesMicrophoneAlarm(warning)) {
        this.microphoneWarning =
          'The patient is not hearing you — your microphone has stopped sending audio. Check your headset.';
      }
    });

    this.phoneTypes = Object.values(this.dataService.phoneTypes);
    this.existingPatient = this.data.existingPatient;

    if (this.existingPatient && !this.data.isExternal) {
      this.patientId = this.data.id;
      this.patientContacts$ = this.fsService.colWithIds$<PatientContact[]>(`users/${this.patientId}/my_contacts`).pipe(
        takeUntil(this.dialogRef.afterClosed()),
        tap((data) => {
          if (data.length > 0) {
            const primaryContact = data.find((contact) => contact.isPrimary === true);
            if (primaryContact) {
              this.callForm.get('contactNumber').setValue(primaryContact);
            } else {
              this.callForm.get('contactNumber').setValue(data[0]);
            }
          }
        })
      );
    } else if (this.data.isExternal && this.data?.phone) {
      this.callForm.get('contactNumber').setValue(this.data?.phone);
    }

    setTimeout(() => {
      if (!this.callForm.get('isIdentityVerified').value) {
        this.shouldRemind = true;
      }
      if (!this.callForm.get('isRecordConsent').value) {
        this.shouldRemindRecordConsent = true;
      }
    }, 10000);
  }

  initializeForm(): void {
    this.callForm = this.fb.group({
      contactNumber: ['', Validators.required],
      twilioNumber: ['', Validators.required],
      twilioNumberPerClient: [''],
      notes: ['', Validators.required],
      follow_up: [''],
      via: ['browser'],
      isIdentityVerified: [false],
      isRecordConsent: [false],
      // Defaults to leaving a live message, which is exactly how calls behaved before a message
      // could be pre-selected — so a staff member who ignores this control notices no change.
      voicemailMessageId: [VOICEMAIL_SELECTION.LIVE],
    });
  }

  getIconFromType(contactType: string): string {
    let phoneType;
    if (this.phoneTypes && contactType) {
      phoneType = this.phoneTypes.find((pT) => pT.value === contactType);
    }
    return phoneType ? phoneType.icon : 'phone';
  }

  getContactTooltip(contact: PatientContact): string {
    if (!contact) {
      return '';
    }
    const parts: string[] = [];
    if (contact.contact_description) {
      parts.push(contact.contact_description);
    }
    const dayLabels: Record<string, string> = {
      mon: 'Mon',
      tue: 'Tue',
      wed: 'Wed',
      thu: 'Thu',
      fri: 'Fri',
      sat: 'Sat',
      sun: 'Sun',
    };
    const days = CONTACT_DAY_ORDER.filter((d) => (contact.best_times_days || []).includes(d)).map((d) => dayLabels[d] ?? d);
    const rawPeriod = contact.best_times_period;
    const periodArray: string[] = Array.isArray(rawPeriod) ? rawPeriod : rawPeriod ? [rawPeriod] : [];
    const periods = periodArray.map((p) => p.charAt(0).toUpperCase() + p.slice(1));
    if (days.length || periods.length) {
      const timeParts: string[] = [];
      if (days.length) {
        timeParts.push(days.join(', '));
      }
      if (periods.length) {
        timeParts.push(periods.join(' & '));
      }
      parts.push(`Best times: ${timeParts.join(' · ')}`);
    }
    return parts.join('\n');
  }

  async doCall(): Promise<void> {
    // The clinic line arrives asynchronously and overrides the care manager's line. Dialing inside
    // that window would send the personal line while the panel still shows "Resolving caller ID…" —
    // the one path where what is displayed and what is sent genuinely diverge.
    if (this.callerIdResolving) {
      this.snackBarService.genericSnackBar(
        'Still resolving the caller ID, please try again in a moment.',
        ['warn-snackbar'],
        5000
      );
      return;
    }
    if (this.blocksCallWithoutCallerId) {
      // Two different situations with two different remedies: the staff can pick a line when the
      // client has some, and only an administrator can help when it has none.
      this.snackBarService.genericSnackBar(
        this.awaitingCallerLineChoice
          ? 'Select the clinic number to call from before placing the call.'
          : 'No caller ID is assigned for this call. Please contact an administrator.',
        ['error-snackbar'],
        5000
      );
      return;
    }
    // The sender side is fully covered by the hasCallerId guard above: effectiveCallerId is what gets
    // sent as `from`, so re-checking authService.user.twilio_line here would reject a valid clinic
    // line just because the care manager has no personal one.
    const to = this.callForm.get('contactNumber').value;
    if (this.usesClientLineSelector) {
      if (to) {
        this.callingInProgress = true;
        await this.browserCall();
      } else {
        this.snackBarService.genericSnackBar('Please verify client phone number', ['error-snackbar'], 5000);
      }
    } else {
      if (to && this.authService.user) {
        this.callingInProgress = true;
        await this.browserCall();
      } else {
        this.snackBarService.genericSnackBar(
          'Please verify sender and patient contact number',
          ['error-snackbar'],
          5000
        );
      }
    }
  }

  showMicrophoneHelp(): void {
    if (isMicrophoneUnavailable(this.microphoneAvailability)) {
      this.twilioService.openMicrophoneRequiredDialog(this.microphoneAvailability);
    }
  }

  async browserCall(): Promise<void> {
    // First make this tab the one holding the Twilio Device and give it a moment to register. Only one
    // Welby tab holds it at a time, and the microphone check below needs the Device too.
    let readiness: CallReadiness;
    try {
      readiness = await this.twilioService.prepareForCall();
    } catch (error) {
      console.error('Preparing the browser call failed:', error);
      this.twilioService.cancelPreparedCall();
      readiness = 'unavailable';
    }
    if (this.destroyed) {
      this.twilioService.cancelPreparedCall();
      return;
    }
    if (readiness !== 'ready') {
      this.snackBarService.genericSnackBar(CALL_READINESS_MESSAGES[readiness], ['error-snackbar'], 8000);
      this.callingInProgress = false;
      return;
    }

    // Before anything is dialled: a call placed on a microphone that captures nothing looks entirely
    // normal to everyone except the patient, who hears silence while the care manager talks. The
    // check also re-acquires the device, which is what repairs the common case.
    let microphone: MicrophoneCheckResult;
    let availability: MicrophoneAvailability;
    try {
      microphone = await this.twilioService.ensureMicrophoneReady({
        patientId: this.patientId,
        fromLine: this.effectiveCallerId,
      });
      // Read after the check, so it is the state at the moment of dialling. A refusal the browser
      // reports overrides the check: once access is blocked from the address bar, Chrome can keep
      // handing out a near-silent track until the page is reloaded, which the check would grade
      // `very-quiet` — a warning, not a refusal. Only the refusal: an empty device list here is not
      // trusted over a check that just opened the microphone successfully.
      availability = await this.microphoneAvailabilityService.refresh();
    } catch (error) {
      console.error('Checking the microphone failed:', error);
      this.twilioService.cancelPreparedCall();
      if (this.destroyed) {
        this.twilioService.releaseMicrophone();
      } else {
        this.abandonDial('The call could not be placed. Please try again.');
      }
      return;
    }
    if (this.destroyed) {
      // The check left the microphone selected; nothing else will release it now.
      this.twilioService.cancelPreparedCall();
      this.twilioService.releaseMicrophone();
      return;
    }
    if (availability === 'permission-denied') {
      this.twilioService.cancelPreparedCall();
      this.callingInProgress = false;
      this.twilioService.openMicrophoneRequiredDialog('permission-denied');
      return;
    }
    if (isMicrophoneProblem(microphone.status)) {
      // Every outcome that stops the call gets the required-microphone alert, with its steps: an 8 s
      // snackbar was too easy to miss, and the care manager was left pressing Call again.
      this.twilioService.cancelPreparedCall();
      this.callingInProgress = false;
      this.twilioService.openMicrophoneRequiredDialog(microphone.status);
      return;
    }
    if (isReportableMicrophoneFailure(microphone.status)) {
      const blocking = isBlockingMicrophoneFailure(microphone.status);
      this.snackBarService.genericSnackBar(
        MICROPHONE_CHECK_MESSAGES[microphone.status],
        [blocking ? 'error-snackbar' : 'warn-snackbar'],
        8000
      );
      if (blocking) {
        this.twilioService.cancelPreparedCall();
        this.callingInProgress = false;
        return;
      }
    }

    const senderLine = this.effectiveCallerId;
    // Snapshot: from here on the in-call UI shows these values, not the reactive getters.
    this.dialedCallerId = senderLine;
    this.dialedCallerIdSourceLabel = this.callerIdSourceLabel;
    const sender = { ...this.authService.user, twilio_line: senderLine };

    const rawNumber = this.getContactLine(this.callForm.get('contactNumber').value)?.replace(/\D/g, '') || '';
    const formattedTo = rawNumber.startsWith('1') ? `+${rawNumber}` : `+1${rawNumber}`;

    // The value is committed here, at connect. The fields stay visible for the whole ringing period —
    // they are only hidden once the call is accepted — so without this the control would still look
    // editable while a change to it was already being ignored, and its hint invites exactly that.
    if (this.canChooseVoicemailMessage) {
      this.callForm.get('voicemailMessageId').disable({ emitEvent: false });
    }

    this.twilioService
      .makeBrowserCall({
        To: formattedTo,
        from: sender.twilio_line,
        careManagerFullName: `${sender.firstName || ''} ${sender.lastName || ''}`.trim(),
        patientId: this.patientId,
        // Omitted entirely outside the pilot — its absence is what keeps detection off the call.
        // Read via `getRawValue` because the control is disabled just above, and `value` skips
        // disabled controls.
        ...(this.canChooseVoicemailMessage && {
          voicemailMessageId: this.callForm.getRawValue().voicemailMessageId,
        }),
      })
      .then((call: Call) => {
        if (this.destroyed) {
          // Closed while the SDK was still connecting: close() had no call to hang up yet, and the
          // listeners that release the microphone were never attached. The SDK refuses to unset the
          // input device while the call is up, so it is released once the hang-up completes.
          if (call) {
            call.on('disconnect', () => this.twilioService.releaseMicrophone());
            call.disconnect();
          } else {
            this.twilioService.releaseMicrophone();
          }
          return;
        }
        this.currentCall = call;
        if (!call) {
          // The service swallows a connect failure and resolves with null, so this — not the catch
          // below — is how a dial usually fails, and it has to undo exactly as much.
          this.abandonDial('The call could not be placed. Please try again.');
          return;
        }
        this.setUpCallListeners(call);
      })
      .catch((error: Error) => {
        console.error('Call initiation failed:', error);
        this.abandonDial(`Failed to initiate call: ${error.message || 'Unknown error'}`);
      });
  }

  getContactLine(contact: PatientContact | string): string {
    if (typeof contact === 'string') {
      return contact;
    } else {
      return contact?.contact_data;
    }
  }

  sendDigits(digits: string): void {
    if (this.currentCall) {
      this.currentCall.sendDigits(digits);
    } else {
      this.snackBarService.genericSnackBar('No active connection.', ['error-snackbar'], 5000);
      console.error('No active connection.');
    }
  }

  setUpCallListeners(call: Call): void {
    if (call) {
      this.callQualitySubscription?.unsubscribe();
      this.callQuality = INITIAL_CALL_QUALITY;
      this.callQualitySubscription = this.callQualityService
        .watch(call)
        .pipe(takeUntil(this.unsubscribe))
        .subscribe((quality) => (this.callQuality = quality));
      call.on('disconnect', () => {
        this.callQualitySubscription?.unsubscribe();
        this.callQualitySubscription = null;
        this.rememberEndedCall(call);
        if (this.duration > 0 && !this.data.isExternal) {
          this.saveInCommunicationHistory(this.duration);
          this.offerPostCallSurvey(this.postCallSurveyRegistered ? this.parentCallSid : null, this.patientId);
        }
        this.callInProgress = false;
        this.microphoneWarning = null;
        // Hands the microphone back so the browser stops showing the recording indicator between
        // calls, and so the next call opens the device again instead of reusing this call's track.
        this.twilioService.releaseMicrophone();
        // A call that ends before being accepted (no answer / busy / rejected) never runs the
        // 'accept' handler, so without this the component stays stuck in callingInProgress: the Call
        // button keeps its spinner and displayedCallerId would report no caller ID at all.
        this.callingInProgress = false;
        this.isMuted = false;
        this.resetHoldState();
        this.maximizeDialog();
        this.duration = 0;
        this.postCallSurveyRegistered = false;
        this.durationFormatted = '00:00';
        // Back to the live default for the next call. The dialog stays open and redialable, so a
        // selection left standing would follow the staff member onto a different number — and the
        // number field sits above this control, so they have no reason to look back at it. Leaving a
        // program-specific message on whoever answers next is a disclosure, not a mis-set flag.
        this.callForm.get('voicemailMessageId').enable({ emitEvent: false });
        this.callForm.get('voicemailMessageId').setValue(VOICEMAIL_SELECTION.LIVE);
        this.dialedCallerId = null;
        this.dialedCallerIdSourceLabel = null;
        // The Call object is dead once disconnected. It was only cleared in endCall()/close(), so a
        // call that failed after device.connect() resolved left a stale one behind, which then made
        // sendDigits/mute act on a dead call.
        this.currentCall = null;
        clearInterval(this.durationInterval);
      });
      call.on('accept', () => {
        this.callInProgress = true;
        this.callingInProgress = false;
        this.parentCallSid = call.parameters.CallSid;
        this.updateRecordConsent(false);
        this.registerPostCallSurvey();
        this.dialogRef.updatePosition({ bottom: '0', right: '0' });
        this.removeBackDrop();
        this.durationInterval = setInterval(() => {
          this.duration = this.duration + 1;
          this.durationFormatted = this.displayTime(this.duration);
        }, 1000);
      });
    }
  }

  /**
   * Lets the clinical time form link a "spoke with the patient" entry to this call, for the short-call
   * warning. Only calls to a known patient count: external numbers have no patient to link to.
   * Recorded whether or not the call was answered, because an unanswered call is exactly the one the
   * warning is for.
   */
  private rememberEndedCall(call: Call): void {
    const callSid = call?.parameters?.CallSid;
    const nurseUid = this.authService.user?.user_id;
    if (!callSid || !this.patientId || this.data?.isExternal || !nurseUid) {
      return;
    }
    this.lastPatientCall.record({ callSid, patientId: this.patientId, nurseUid, endedAt: Date.now() });
  }

  endCall(): void {
    this.contentVisible = true;
    this.microphoneWarning = null;
    this.addBackDrop();
    this.currentCall?.disconnect();
    this.currentCall = null;
    this.isMuted = false;
    this.resetHoldState();
    this.dialedCallerId = null;
    this.dialedCallerIdSourceLabel = null;
    this.maximizeDialog();
  }

  /**
   * @param patientId The patient the call went to. The survey dialog acts on whichever chart is open
   *                  when the button is clicked, so it opens only if that is still this patient's chart.
   */
  showSnackBar(patientId?: string): void {
    const snackBarRef = this.snackBarService.genericSnackBar(
      'A survey is available, do you want to send it to the patient?',
      ['success-snackbar'],
      10000,
      `Send Survey`,
      'right',
      'top'
    );
    snackBarRef.onAction().subscribe(() => {
      if (patientId && this.patientService.currentPatientServiceID !== patientId) {
        this.warnSurveyNotSentForPreviousCall();
        return;
      }
      this.openSurveyDialog();
    });
  }
  saveNotes(): void {
    const resolution: ClinicalResolution = {
      assessor: this.authService.user.user_id,
      content: this.callForm.get('notes').value,
      follow_up: this.callForm.get('follow_up').value,
      date: firebase.firestore.Timestamp.fromDate(new Date()),
      associations: {
        uid: this.patientService?.currentPatientServiceID,
        client_id: this.patientService?.currentPatientService?.client_responsible_id,
      },
    };
    this.fsService.add(`clinical_assessments`, resolution).then((resp) => {
      this.snackBarService.genericSnackBar('Notes added', ['success-snackbar']);
      this.callForm.get('notes').reset();
      this.callForm.get('follow_up').reset();
    });
  }
  close(): void {
    this.dialogRef.close();
    this.currentCall?.disconnect();
    this.currentCall = null;
  }

  openSurveyDialog() {
    const dialogRef = this.dialog.open(SurveyDialogComponent, {
      data: {},
      width: '650px',
      height: '80vh',
      panelClass: 'welby-modal-scroll',
    });
  }

  displayTime(seconds: number): string {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes?.toString().padStart(2, '0')}:${remainingSeconds?.toString().padStart(2, '0')}`;
  }

  toggleSize() {
    if (!this.contentVisible) {
      this.maximizeDialog();
    } else {
      this.minimizeDialog();
    }
  }

  onDragEnded(event: any) {
    if (this.contentVisible) {
      this.dragPosition = event.source.getFreeDragPosition();
    }
  }

  minimizeDialog() {
    this.contentVisible = false;
    this.dialogRef.updatePosition({
      bottom: '0',
      right: '0',
    });
    this.dragPosition = { x: 0, y: 0 };
    this.resetDialogTransform();
    this.removeBackDrop();
    this.hideDialogContainer();
  }

  maximizeDialog() {
    this.contentVisible = true;
    this.dialogRef.updatePosition();
    this.dragPosition = { x: 0, y: 0 };
    this.resetDialogTransform();
    this.showDialogContainer();
  }

  resetDialogTransform() {
    const dialogEl = document.querySelector('.cdk-overlay-pane');
    if (dialogEl) {
      (dialogEl as HTMLElement).style.transform = '';
    }
  }

  removeBackDrop(): void {
    const backdrop = document.querySelector('.cdk-overlay-backdrop');
    backdrop.classList.add('cdk-overlay-backdrop-hide');
  }

  addBackDrop(): void {
    const backdrop = document.querySelector('.cdk-overlay-backdrop');
    backdrop.classList.remove('cdk-overlay-backdrop-hide');
  }

  toggleIdentityVerified() {
    if (this.callForm.get('isIdentityVerified').value) {
      this.shouldRemind = false;
    }
  }

  toggleRecordConsent(): void {
    this.shouldRemindRecordConsent = false;
    const recordConsent = this.callForm.get('isRecordConsent').value;

    if (this.callInProgress && this.parentCallSid) {
      this.updateRecordConsent(recordConsent);
    }
  }

  updateRecordConsent(recordConsent: boolean): void {
    if (!this.parentCallSid) {
      return;
    }
    this.twilioService.updateRecordConsent(this.parentCallSid, recordConsent).subscribe({
      error: (error) => {
        console.error('Failed to update record consent on active call:', error);
        this.callForm.get('isRecordConsent').setValue(!recordConsent);
        this.snackBarService.genericSnackBar(
          'Failed to update recording consent. Please try again.',
          ['error-snackbar'],
          5000
        );
      },
    });
  }

  /**
   * Links this call to its patient so the backend can send the NPS survey once the call ends. Only
   * calls to a patient carry a `patientId`; external numbers are never surveyed. A failure costs the
   * survey and nothing else, so the staff member is not interrupted with it.
   */
  registerPostCallSurvey(): void {
    this.postCallSurveyRegistered = false;
    const callSid = this.parentCallSid;
    if (!this.patientId || !callSid) {
      return;
    }
    this.twilioService.registerPostCallSurvey(callSid, this.patientId).subscribe({
      // A late answer for a call that is already over must not mark the next one as registered.
      next: () => (this.postCallSurveyRegistered = this.parentCallSid === callSid),
      error: (error) => console.error('Failed to register the post-call survey:', error),
    });
  }

  /**
   * After the hang-up. A call registered for the automatic survey waits for the backend's verdict:
   * - `sent`: the staff member is told, and the manual survey is not offered;
   * - still being sent (`processing`), or a send that got no answer (`send-unconfirmed`): it may already
   *   have reached the patient, so the manual survey is not offered either;
   * - anything else — skipped, refused, or no report at all: the manual survey is offered, so a backend
   *   failure still leaves the staff member a way to send it.
   * A call that was not registered goes straight to the manual survey.
   *
   * Not tied to the dialog's lifetime on purpose: the wait is bounded, and closing the dialog right
   * after hanging up must not cost the staff member the prompt.
   */
  offerPostCallSurvey(callSid: string | null, patientId: string | undefined): void {
    if (!callSid) {
      this.offerManualSurvey(patientId);
      return;
    }
    this.twilioService.waitForPostCallSurveyOutcome(callSid).subscribe((outcome) => {
      if (outcome?.status === 'sent') {
        this.snackBarService.genericSnackBar(
          'The NPS survey was sent to the patient automatically',
          ['success-snackbar'],
          8000
        );
        return;
      }
      if (outcome?.status === 'processing' || outcome?.reason === 'send-unconfirmed') {
        this.snackBarService.genericSnackBar(
          'The NPS survey for the last call may already have reached the patient',
          ['warn-snackbar'],
          8000
        );
        return;
      }
      this.offerManualSurvey(patientId);
    });
  }

  /**
   * The manual survey prompt, offered only when the patient is due a survey.
   *
   * The prompt's survey dialog acts on the chart that is open, and it can appear seconds after the
   * hang-up, by when the staff member may have opened another patient: it is offered only while the
   * called patient's chart is still the open one. `lastSurveyDate` is read fresh, because the backend
   * writes it before an automatic send and the chart's copy predates the call.
   */
  async offerManualSurvey(patientId: string | undefined): Promise<void> {
    if (patientId && this.patientService.currentPatientServiceID !== patientId) {
      this.warnSurveyNotSentForPreviousCall();
      return;
    }

    let enabled = true;
    try {
      // A one-shot server read: `doc$().pipe(take(1))` can answer from the local cache.
      const patient = patientId
        ? await this.fsService
            .doc<Patient>(`users/${patientId}`)
            .get()
            .pipe(map((snapshot) => snapshot.data()))
            .toPromise()
        : this.patientService.currentPatientService;
      enabled = await this.utilService.isSurveyEnabled(
        patient?.lastSurveyDate,
        patient?.client_responsible_id ?? this.patientService.currentPatientService?.client_responsible_id
      );
    } catch (error) {
      // The check is a convenience for the staff member; when it cannot be made, the prompt is offered
      // as it always was.
      console.error('Could not check whether a survey is due:', error);
    }
    if (enabled) {
      this.showSnackBar(patientId);
      return;
    }
    this.snackBarService.genericSnackBar(
      'Survey is not enabled for this patient. Last survey was sent within 30 days',
      ['warn-snackbar'],
      8000
    );
  }

  toggleMute(): void {
    if (this.currentCall) {
      this.isMuted = !this.isMuted;
      this.currentCall.mute(this.isMuted);
    }
  }

  /**
   * Toggles hold on the patient's leg.
   *
   * Unlike mute — which is local to the browser leg and instant — hold is a REST round-trip that
   * may also promote the call to a Twilio conference (~1-2s audio gap the first time). The button
   * therefore stays disabled until the backend confirms, and the visual state only flips on
   * success: a button that lied about hold would leave a patient hearing the room.
   *
   * Hold and mute are independent (the mic stays as the nurse left it) — they act on different legs.
   */
  toggleHold(): void {
    if (!this.parentCallSid || this.holdPending) {
      return;
    }

    const nextHold = !this.isOnHold;
    this.holdPending = true;

    this.twilioService
      .setCallHold(this.parentCallSid, nextHold)
      .pipe(take(1), takeUntil(this.holdCancel$))
      .subscribe({
        next: (result) => {
          this.isOnHold = result.hold;
          this.holdPending = false;
        },
        error: () => {
          this.holdPending = false;
          this.snackBarService.genericSnackBar(
            nextHold ? 'Could not put the call on hold. Please try again.' : 'Could not resume the call. Please try again.',
            ['error-snackbar'],
            5000
          );
        },
      });
  }

  toggleAddParticipant(): void {
    this.showAddParticipant = !this.showAddParticipant;
    if (!this.showAddParticipant) {
      this.additionalParticipantPhoneNumberControl.reset();
    } else {
      setTimeout(() => {
        const inputElement = document.querySelector('.participant-input input') as HTMLInputElement;
        if (inputElement) {
          inputElement.focus();
        }
      }, 100);
    }
  }

  validatePhoneNumber(control: UntypedFormControl): { [key: string]: any } | null {
    const value = control.value;
    if (!value) {
      return null;
    }

    const digits = value.replace(/\D/g, '');
    const isTollFree = /^1?800/.test(digits);

    if (isTollFree) {
      return digits.length >= 10 && digits.length <= 11 ? null : { invalidPhone: true };
    }
    return digits.length === 10 ? null : { invalidPhone: true };
  }

  formatPhoneNumber(control: UntypedFormControl): void {
    if (!control.value) {
      return;
    }
    if (control.valid && control.value) {
      const formatted = this.utilService.formatPhoneNumberStringConferenceCalling(control.value);
      control.setValue(formatted, { emitEvent: false });
    }
  }

  handleEnterKey(event: KeyboardEvent): void {
    if (this.additionalParticipantPhoneNumberControl.valid) {
      this.formatPhoneNumber(this.additionalParticipantPhoneNumberControl);
      this.addParticipant();
    }
  }
  async addParticipant(): Promise<void> {
    if (this.additionalParticipantPhoneNumberControl.valid) {
      const rawNumber = this.additionalParticipantPhoneNumberControl.value.replace(/\D/g, '');
      const formattedNumber = `+1${rawNumber}`;
      // Same line as the main leg. Reading the control by hand returned '' on external calls,
      // where the number lives in twilioNumberPerClient.
      const from: string = this.dialedCallerId;
      if (!this.parentCallSid || !from) {
        this.snackBarService.genericSnackBar(
          'The call is no longer active; cannot add a participant.',
          ['error-snackbar'],
          5000
        );
        return;
      }

      this.twilioService.addParticipantToCall(this.parentCallSid, formattedNumber, from).subscribe({
        next: (response) => {
          this.additionalParticipantNumber =
            '+1 ' + this.utilService.formatPhoneNumberStringConferenceCalling(formattedNumber);
          this.additionalParticipantPhoneNumberControl.reset();
          this.additionalParticipantCallSid = response.data.addedParticipantCallSid;
          this.additionalParticipantSessionId = this.additionalParticipantCallSid;

          this.subscribeToParticipantCallLeg(this.additionalParticipantSessionId, this.additionalParticipantCallSid);
        },
        error: (error) => {
          console.error('Failed to add participant:', error);
          this.snackBarService.genericSnackBar(
            'Failed to add participant. Please try again.',
            ['error-snackbar'],
            5000
          );
        },
      });

      this.additionalParticipantPhoneNumberControl.reset();
      this.showAddParticipant = false;
    }
  }

  hangUpParticipant(): void {
    if (this.additionalParticipantCallSid) {
      this.twilioService.hangUpParticipant(this.parentCallSid, this.additionalParticipantCallSid).subscribe({
        next: (response) => {
          this.cleanUpAdditionalParticipant();
        },
        error: (error) => {
          console.error('Failed to hang up participant:', error);
          this.snackBarService.genericSnackBar(
            'Failed to hang up on participant. Please try again.',
            ['error-snackbar'],
            5000
          );
        },
      });
    }
  }

  cleanUpAdditionalParticipant(): void {
    this.additionalParticipantCallSid = null;
    this.additionalParticipantNumber = null;
    this.additionalParticipantSessionId = null;
  }

  initializeTwilioNumber(): void {
    if (this.isSingleTwilioLinePerClient && this.data?.isExternal) {
      this.callForm.get('twilioNumber').clearValidators();
      this.callForm.get('twilioNumber').updateValueAndValidity();
      this.callForm.get('twilioNumberPerClient').setValidators(Validators.required);
      this.callForm.get('twilioNumberPerClient').updateValueAndValidity();
      this.callerIdResolving = true;
      this.userService
        .getClientPhones(this.authService.user.user_id)
        .pipe(retry(1), take(1))
        .subscribe({
          next: (clientPhoneConfigs: PhoneConfig[]) => {
            this.callerIdResolving = false;
            this.clientPhoneConfigList = clientPhoneConfigs.filter((config) => config.isActive);
            if (this.clientPhoneConfigList && this.clientPhoneConfigList.length > 0) {
              this.callForm.get('twilioNumberPerClient').setValue(this.clientPhoneConfigList[0].phoneNumber);
            }
          },
          error: (error) => {
            this.callerIdResolving = false;
            console.error('Error fetching client phone configs:', error);
            this.snackBarService.genericSnackBar('Error fetching twilio numbers', ['error-snackbar'], 5000);
          },
        });
    } else if (!this.data?.isExternal) {
      this.callForm.get('twilioNumber').setValue(this.provider?.twilio_line[0]);
      const clientId = this.data?.client_responsible_id;
      if (clientId) {
        this.callerIdResolving = true;
        this.patientService
          .getClientPatient(clientId)
          .pipe(takeUntil(this.unsubscribe))
          .subscribe({
            next: (client: Client) => {
              // Everything the caller-ID panel needs from this document, and nothing else: it
              // describes the line, it never chooses it. The choice below is NP-04's.
              this.captureClientDescription(client);
              // Read off the **patient's client**, gating the clinic-lines dropdown. Not the same flag
              // as `this.isSingleTwilioLinePerClient` above, which carries the identical name on the
              // logged-in **user** document and gates the external-call path instead. Both predate
              // NP-04 (PR #1011); the collision is inherited, not introduced here.
              if (!client?.featureToggles?.isSingleTwilioLinePerClient) {
                this.callerIdResolving = false;
                return;
              }
              this.clinicPhoneOptions = getActiveClientPhoneConfigs(client);
              if (!this.clinicPhoneOptions.length) {
                this.callerIdResolving = false;
                console.error('This client has the feature enabled but no active phone line configured.');
                this.snackBarService.genericSnackBar(
                  "This patient's client has no phone line configured.",
                  ['error-snackbar'],
                  5000
                );
                return;
              }
              // Stays resolving across this second hop: applyDerivedCallerId owns the flag from here.
              this.applyDerivedCallerId();
            },
            error: (error) => {
              this.callerIdResolving = false;
              console.error('Error fetching client:', error);
              this.snackBarService.genericSnackBar('Error fetching client information', ['error-snackbar'], 5000);
            },
          });
      }
    } else {
      this.callForm.get('twilioNumber').setValue(this.provider?.twilio_line[0]);
    }
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.unsubscribe.next();
    this.unsubscribe.complete();
    this.cleanupParticipantSubscription();
    this.resetHoldState();
    // Only here, never in resetHoldState: that also runs whenever a call ends, and this dialog
    // outlives a call — a completed Subject would make takeUntil short-circuit the next hold.
    this.holdCancel$.complete();
    // Only the 'disconnect' handler cleared this, so closing the dialog mid-call left a 1s interval
    // holding a reference to the destroyed component.
    clearInterval(this.durationInterval);
  }

  /**
   * Clears the hold state so a finished call never leaves the button stuck showing "Resume".
   *
   * Also cancels any in-flight hold request: this dialog outlives a single call, so a response
   * landing after the call ended would otherwise set isOnHold for the NEXT call.
   */
  /**
   * Puts the dialog back where it was before the dial, and says so.
   *
   * Both ways a dial can fail end here, because they need identical cleanup and previously did not:
   * the service resolves with `null` on a connect failure and only rejects for an error thrown
   * before it catches, so the null path is the common one and was the one left half-done.
   *
   * Every line undoes something `browserCall` did on the way out. The microphone was acquired for a
   * call that never happened and only a live Call's 'disconnect' would have released it; the
   * voicemail control was disabled at dial time and the same handler would have re-enabled it; the
   * caller-ID snapshot describes a call that does not exist; and `callingInProgress` is what holds
   * the Call button disabled with its spinner — without resetting it the care manager cannot dial
   * again without reopening the dialog, and never learns the call did not go out.
   */
  private abandonDial(message: string): void {
    this.twilioService.releaseMicrophone();
    this.callForm.get('voicemailMessageId').enable({ emitEvent: false });
    this.snackBarService.genericSnackBar(message, ['error-snackbar'], 5000);
    this.callInProgress = false;
    this.callingInProgress = false;
    this.dialedCallerId = null;
    this.dialedCallerIdSourceLabel = null;
  }

  private resetHoldState(): void {
    this.holdCancel$.next();
    this.isOnHold = false;
    this.holdPending = false;
  }

  /**
   * Whether a number belongs to the patient's client rather than to the care manager.
   *
   * The option list is checked as well as `clinicPhoneNumber`, and that is not redundant: the
   * dropdown writes the staff's override straight to the `twilioNumber` control and leaves
   * `clinicPhoneNumber` on the previously derived line, so an overridden client line would otherwise
   * be labelled as the care manager's own.
   */
  private isClientOwnLine(phoneNumber: string): boolean {
    if (!phoneNumber) {
      return false;
    }
    return (
      phoneNumber === this.clinicPhoneNumber ||
      this.clinicPhoneOptions.some((option) => option.phoneNumber === phoneNumber)
    );
  }

  /**
   * The only thing the caller-ID panel reads from the `clients/{id}` document: how to describe the
   * line, never which line it is — that is the backend's decision, applied by `applyDerivedCallerId`.
   */
  private captureClientDescription(client: Client): void {
    this.clientName = client?.client_name ?? null;
    this.clientRequiresOwnLine = !!client?.featureToggles?.isSingleTwilioLinePerClient;
  }

  /**
   * Asks the backend which line to call this patient from and preselects it (NP-04 step 2).
   *
   * Falls back to the step 1 behaviour — preselect only when there is a single line, otherwise leave
   * the control empty and let `twilioNumber`'s required validator force a choice — whenever the
   * derivation is unavailable or yields nothing. A routing rule that cannot answer must never be the
   * reason a nurse cannot place a call.
   */
  private applyDerivedCallerId(): void {
    if (!this.patientId) {
      this.preselectWithoutDerivation();
      return;
    }

    // Owns the flag from here: the panel must keep saying "resolving" across this hop rather than
    // show the care manager's line for the instant before the derived one lands. Every terminal path
    // below — including the two inside applyCallerIdDecision — clears it via preselectWithoutDerivation
    // or setPreselectedCallerLine.
    this.callerIdResolving = true;

    this.twilioService
      .getOutboundCallerIdDecision(this.patientId)
      .pipe(take(1), takeUntil(this.unsubscribe))
      .subscribe({
        next: (decision) => this.applyCallerIdDecision(decision),
        error: (error) => {
          console.error('Error deriving the outbound caller ID:', error);
          this.preselectWithoutDerivation();
        },
      });
  }

  private applyCallerIdDecision(decision: OutboundCallerIdDecision): void {
    // A derived number that is not among the lines this dialog is showing would put the control in a
    // state the dropdown cannot display — value set, nothing highlighted. That happens if the client
    // document and the backend disagree for a moment, so treat it as "no derivation" rather than
    // preselecting something invisible.
    const derivedEntry = decision?.phoneNumber
      ? this.clinicPhoneOptions.find((option) => option.phoneNumber === decision.phoneNumber)
      : undefined;

    if (!derivedEntry) {
      this.preselectWithoutDerivation();
      return;
    }

    this.clinicPhoneNumber = derivedEntry.phoneNumber;
    this.setPreselectedCallerLine(derivedEntry.phoneNumber);
  }

  /** The step 1 rule, kept as the fallback for every path where no line could be derived. */
  private preselectWithoutDerivation(): void {
    const onlyOption = this.clinicPhoneOptions.length === 1 ? this.clinicPhoneOptions[0] : null;
    this.clinicPhoneNumber = onlyOption?.phoneNumber ?? null;
    this.setPreselectedCallerLine(this.clinicPhoneNumber ?? '');
  }

  /**
   * Applies the derived line to the form.
   *
   * It used to record the entry id alongside it, so accepting the preselection could be told apart
   * from overriding it. With one line per clinic there is nothing to override and nothing to persist,
   * so only the number is left.
   */
  private setPreselectedCallerLine(phoneNumber: string): void {
    this.callForm.get('twilioNumber').setValue(phoneNumber);
    // The single exit of the whole resolution: every path that settles on a line (derived, single
    // option, or none) ends here, so the panel stops saying "resolving" exactly when there is
    // something final to show — including when that is nothing.
    this.callerIdResolving = false;
  }

  private hideDialogContainer(): void {
    const container = document.querySelector('.cdk-overlay-pane mat-dialog-container') as HTMLElement;
    if (container) {
      container.style.background = 'transparent';
      container.style.boxShadow = 'none';
      container.style.padding = '0';
    }
  }

  private showDialogContainer(): void {
    const container = document.querySelector('.cdk-overlay-pane mat-dialog-container') as HTMLElement;
    if (container) {
      container.style.background = '';
      container.style.boxShadow = '';
      container.style.padding = '';
    }
  }

  private subscribeToParticipantCallLeg(sessionId: string, callSid: string): void {
    this.participantDestroy$ = new Subject<void>();

    this.twilioService
      .subscribeToParticipantCallLeg(sessionId, callSid)
      .pipe(takeUntil(this.participantDestroy$))
      .subscribe({
        next: (session) => {
          if (!session?.callLegs) {
            return;
          }

          const callLeg = session.callLegs[callSid];
          if (!callLeg) {
            return;
          }

          if (callLeg.status === 'completed') {
            this.removeParticipantFromUI();
            this.cleanupParticipantSubscription();
          }
        },
        error: (err) => {
          console.error('Firestore subscription error', err);
        },
      });
  }

  private removeParticipantFromUI(): void {
    this.additionalParticipantCallSid = null;
    this.additionalParticipantNumber = null;
  }

  private cleanupParticipantSubscription(): void {
    if (!this.participantDestroy$) {
      return;
    }
    this.participantDestroy$?.next();
    this.participantDestroy$?.complete();
    this.participantDestroy$ = undefined;
  }

  private warnSurveyNotSentForPreviousCall(): void {
    this.snackBarService.genericSnackBar(
      "The NPS survey for the previous call may not have been sent. If needed, send it from that patient's chart.",
      ['warn-snackbar'],
      8000
    );
  }

  private async saveInCommunicationHistory(seconds?: number) {
    const record = this.communicationService.buildCommunicationRecord(
      'Outbound call',
      seconds
        ? `Time: ${(seconds / 60).toFixed(2)} mins. <br>Phone number: ${this.getContactLine(
            this.callForm.get('contactNumber').value
          )?.replace(/-/g, '')}. ${
            this.callForm.get('isIdentityVerified').value ? '<br>Patient identity verified' : ''
          }${this.callForm.get('isRecordConsent').value ? '<br>Recording consent given' : ''} `
        : 'Internal call',
      this.patientService.currentPatientService,
      this.authService.user,
      PatientCommunicationType.CALL,
      null,
      null,
      this.callForm.get('via').value === 'browser' ? this.callForm.get('isIdentityVerified').value : undefined,
      this.callForm.get('via').value === 'browser' ? this.callForm.get('isRecordConsent').value : undefined
    );
    try {
      await this.communicationService.saveCommunicationRecord(
        record,
        this.patientService.currentPatientService.user_id
      );
    } catch (error) {
      this.snackBarService.genericSnackBar(
        'An error occurred while saving communication history',
        ['error-snackbar'],
        5000
      );
      throw error;
    }
  }
}
